/**
 * Sub-User Management Page
 * Combines sub-user list with creation/editing and split configuration.
 * Matches the Sub-User List View and Sub-User Setup mockups.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import useAuthStore from '../lib/auth-store';
import { displayProcessor, uniqueProcessors } from '../lib/processorDisplay';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Users, UserPlus, Percent, Calculator, Search,
  Edit, Trash2, Save, X, ChevronDown, ChevronUp
} from 'lucide-react';

const PROCESSORS = uniqueProcessors(['TSYS-msc', 'North', 'FiservCopilot', 'TSYS', 'Elavon', 'EPX', 'First Data']);

export default function SubUserManagement() {
  const { user } = useAuthStore();
  const [subUsers, setSubUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingSplit, setEditingSplit] = useState(null);

  // Form state
  const [form, setForm] = useState({
    to_user_id: '',
    split_percentage: 50,
    commission_type: 'percentage',
    fixed_amount: 0,
    processors: [],
    apply_same_split: true,
  });

  // Calculator preview
  const [calcGross, setCalcGross] = useState(100);
  const mainBaseRate = editingSplit?.upline_split || 70;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes, availRes] = await Promise.all([
        api.request('/residuals/sub-users'),
        api.request('/residuals/stats'),
        api.request('/residuals/available-users'),
      ]);
      setSubUsers(usersRes);
      setStats(statsRes);
      setAvailableUsers(availRes);
    } catch (err) {
      console.error('Failed to load residuals data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    try {
      await api.request('/residuals/splits', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdate = async () => {
    if (!editingSplit) return;
    try {
      await api.request(`/residuals/splits/${editingSplit.split_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          split_percentage: form.split_percentage,
          commission_type: form.commission_type,
          fixed_amount: form.fixed_amount,
          processors: form.processors,
          apply_same_split: form.apply_same_split,
        }),
      });
      setShowDialog(false);
      setEditingSplit(null);
      resetForm();
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (splitId) => {
    if (!window.confirm('Deactivate this split?')) return;
    try {
      await api.request(`/residuals/splits/${splitId}`, { method: 'DELETE' });
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const resetForm = () => {
    setForm({
      to_user_id: '',
      split_percentage: 50,
      commission_type: 'percentage',
      fixed_amount: 0,
      processors: [],
      apply_same_split: true,
    });
    setEditingSplit(null);
  };

  const openEdit = (subUser) => {
    setEditingSplit(subUser);
    setForm({
      to_user_id: subUser.id,
      split_percentage: subUser.split_percentage,
      commission_type: subUser.commission_type,
      fixed_amount: 0,
      processors: subUser.processors || [],
      apply_same_split: true,
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const toggleProcessor = (proc) => {
    setForm(prev => ({
      ...prev,
      processors: prev.processors.includes(proc)
        ? prev.processors.filter(p => p !== proc)
        : [...prev.processors, proc]
    }));
  };

  const filteredUsers = subUsers.filter(u => {
    const name = `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  // Commission preview calc
  const previewShare = calcGross * (form.split_percentage / 100);
  const previewRemaining = calcGross - previewShare;

  const statCards = [
    { label: 'Total Sub-Users', value: stats.active_sub_users || 0, color: 'bg-blue-50 text-blue-700' },
    { label: 'Avg Split Rate', value: `${stats.avg_split_rate || 0}%`, color: 'bg-green-50 text-green-700' },
    { label: 'Total Revenue', value: `$${(stats.total_revenue || 0).toLocaleString()}`, color: 'bg-purple-50 text-purple-700' },
    { label: 'Reports', value: stats.total_reports || 0, color: 'bg-orange-50 text-orange-700' },
  ];

  return (
    <div className="space-y-6" data-testid="sub-user-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" data-testid="page-title">Sub-User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage revenue splits and commissions for your downline
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-sub-user-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Add Sub-User Split
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="bg-card border rounded-xl p-5" data-testid={`stat-${card.label.toLowerCase().replace(/ /g, '-')}`}>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.color.split(' ')[1]}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search sub-users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-sub-users"
          />
        </div>
      </div>

      {/* Sub-User Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        <table className="w-full" data-testid="sub-users-table">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Name</th>
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</th>
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</th>
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Split %</th>
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Processors</th>
              <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">Loading...</td></tr>
            ) : filteredUsers.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">
                No sub-users found. Click "Add Sub-User Split" to get started.
              </td></tr>
            ) : filteredUsers.map((su) => (
              <tr key={su.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors" data-testid={`sub-user-row-${su.id}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                      {(su.first_name?.[0] || su.email?.[0] || '?').toUpperCase()}
                    </div>
                    <span className="font-medium text-sm">{su.first_name} {su.last_name}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-muted-foreground">{su.email}</td>
                <td className="py-3 px-4">
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium capitalize">
                    {su.role?.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm font-bold text-green-700 bg-green-50 px-2 py-1 rounded-full">
                    {su.split_percentage}%
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap gap-1">
                    {(su.processors || []).length > 0 ? su.processors.map(p => (
                      <span key={p} className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">{displayProcessor(p)}</span>
                    )) : <span className="text-xs text-muted-foreground">All</span>}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    su.split_status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {su.split_status}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(su)} data-testid={`edit-split-${su.id}`}>
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(su.split_id)} data-testid={`delete-split-${su.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setShowDialog(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title">
              {editingSplit ? 'Edit Split Configuration' : 'Create New Sub-User Split'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* User Selection (only for create) */}
            {!editingSplit && (
              <div>
                <label className="text-sm font-medium mb-2 block">Select User</label>
                <Select value={form.to_user_id} onValueChange={v => setForm(p => ({ ...p, to_user_id: v }))}>
                  <SelectTrigger data-testid="select-user-trigger">
                    <SelectValue placeholder="Choose a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableUsers
                      .filter(u => !u.has_split && u.role !== 'super_admin')
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email}) — {u.role?.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Split Configuration */}
            <div className="bg-muted/30 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Percent className="h-4 w-4 text-primary" />
                <h3 className="font-semibold">Residual Split Configuration</h3>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Split Percentage (% of your share)</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={form.split_percentage}
                    onChange={e => setForm(p => ({ ...p, split_percentage: parseFloat(e.target.value) || 0 }))}
                    className="w-28"
                    data-testid="split-percentage-input"
                  />
                  <span className="text-sm font-medium text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  This is the percentage of YOUR share that will be passed to this sub-user.
                </p>
              </div>

              {/* Commission Type */}
              <div>
                <h4 className="text-sm font-medium mb-2">Commission Type</h4>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="commType"
                      checked={form.commission_type === 'percentage'}
                      onChange={() => setForm(p => ({ ...p, commission_type: 'percentage' }))}
                      className="accent-primary"
                    />
                    <span className="text-sm">% of profit (default)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="commType"
                      checked={form.commission_type === 'fixed'}
                      onChange={() => setForm(p => ({ ...p, commission_type: 'fixed' }))}
                      className="accent-primary"
                    />
                    <span className="text-sm">Fixed dollar per merchant</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Processor Assignment */}
            <div className="bg-muted/30 rounded-xl p-5 space-y-3">
              <h3 className="font-semibold text-sm">Assigned Processor Platforms</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PROCESSORS.map(proc => (
                  <label key={proc} className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    form.processors.includes(proc) ? 'border-primary bg-primary/5' : 'border-border hover:border-gray-300'
                  }`}>
                    <input
                      type="checkbox"
                      checked={form.processors.includes(proc)}
                      onChange={() => toggleProcessor(proc)}
                      className="accent-primary"
                    />
                    <span className="text-xs font-medium">{displayProcessor(proc)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Leave unchecked to apply split to all processors.</p>
            </div>

            {/* Commission Calculator Preview */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-blue-600" />
                <h3 className="font-semibold text-sm text-blue-800">Commission Preview</h3>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Gross Profit</p>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs text-muted-foreground">$</span>
                    <Input
                      type="number"
                      value={calcGross}
                      onChange={e => setCalcGross(parseFloat(e.target.value) || 0)}
                      className="h-8 text-sm"
                      data-testid="calc-gross-input"
                    />
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Your Share</p>
                  <p className="text-lg font-bold text-gray-800 mt-1">${calcGross.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Sub-User Payout</p>
                  <p className="text-lg font-bold text-green-700 mt-1">${previewShare.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">You Keep</p>
                  <p className="text-lg font-bold text-blue-700 mt-1">${previewRemaining.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
            <Button
              onClick={editingSplit ? handleUpdate : handleCreate}
              disabled={!editingSplit && !form.to_user_id}
              data-testid="save-split-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {editingSplit ? 'Update Split' : 'Create Split'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
